import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { db, ensureAuth } from '../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  verifyStakeholderTokenCallable,
  readStakeholderClaimsFromToken,
} from '../utils/stakeholderAuth';
import { getPortalAccessMeta, verifyPortalPinServer } from '../utils/portalPinService';
import { ClientPortal } from '../types';
import DesignQuestionnaire from '../components/portal/DesignQuestionnaire';
import QuestionnaireSignupForm from '../components/portal/QuestionnaireSignupForm';
import LoadingScreen from '../components/ui/LoadingScreen';
import {
  createPublicQuestionnaireLead,
  parsePendingLeadParams,
} from '../utils/publicQuestionnaireLead';

/**
 * Resolve portal by slug via access meta + getDoc (slug list queries denied under PIN rules).
 * When a stakeholder invite token is present, verify it FIRST so the claim exists before getDoc.
 */
async function loadPortalSnapBySlug(
  realSlug: string,
  opts?: { stakeholderToken?: string | null },
) {
  const accessMeta = await getPortalAccessMeta(realSlug);
  const stakeholderToken = opts?.stakeholderToken || null;
  if (stakeholderToken) {
    await verifyStakeholderTokenCallable({
      token: stakeholderToken,
      portalId: accessMeta.portalId,
    });
  } else if (!accessMeta.requiresPin) {
    // Questionnaire is not PIN-gated in UI; still need a claim for rules when no PIN is set.
    await verifyPortalPinServer(realSlug, '');
  }
  const snap = await getDoc(doc(db, 'clientPortals', accessMeta.portalId));
  return { accessMeta, snap };
}

const DesignQuestionnairePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');
  const stakeholderTokenParam = searchParams.get('stakeholderToken');

  const initialPendingParams = useRef(parsePendingLeadParams(window.location.search));

  const [portal, setPortal] = useState<ClientPortal | null>(null);
  const [portalDocId, setPortalDocId] = useState<string | null>(null);
  const [isPublicLead, setIsPublicLead] = useState(false);
  const [resolvedProjectId, setResolvedProjectId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStakeholderViewer, setIsStakeholderViewer] = useState(false);
  const [showPendingSignup, setShowPendingSignup] = useState(false);
  const [pendingSignupDefaults, setPendingSignupDefaults] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    location?: string;
  }>();

  const leadCreatedRef = useRef(false);

  // Fetch portal — supports slug lookup OR email-based polling for pending leads
  useEffect(() => {
    ensureAuth();
    const isPending = slug === 'pending';

    const loadPortalData = (docSnap: any) => {
      const data = docSnap.data();
      setPortalDocId(docSnap.id);

      // Questionnaire is a public lead magnet — never gate it behind portal PIN.
      // PIN protection applies to /portal/:slug, not /portal/:slug/questionnaire.

      setIsPublicLead(!!data.isPublicLead || data.currentStage === 'questionnaire');
      if (data.dealId && !projectId) {
        setResolvedProjectId(data.dealId);
      }
      setPortal({
        id: docSnap.id,
        slug: data.slug,
        accessPin: data.accessPin,
        clientName: data.clientName,
        clientEmail: data.clientEmail,
        leadId: data.leadId,
        currentStage: data.currentStage || 'design-fee',
        stageHistory: data.stageHistory || [],
        designFeeSkipped: data.designFeeSkipped || false,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
      } as ClientPortal);
      setLoading(false);
    };

    const fetchBySlug = async () => {
      if (!slug) { setError('Invalid URL'); setLoading(false); return; }
      try {
        await ensureAuth();
        let accessMeta;
        let snap;
        try {
          ({ accessMeta, snap } = await loadPortalSnapBySlug(slug, {
            stakeholderToken: stakeholderTokenParam,
          }));
        } catch (e) {
          if (stakeholderTokenParam) {
            console.error('[Questionnaire] Stakeholder verify failed:', e);
            setError('Invalid or revoked stakeholder link');
            setLoading(false);
            return;
          }
          throw e;
        }
        if (!snap.exists()) { setError('Portal not found'); setLoading(false); return; }
        const docSnap = snap;

        if (stakeholderTokenParam) {
          sessionStorage.setItem(`portal_stakeholder_${slug}`, 'true');
          sessionStorage.setItem(`portal_stakeholder_token_${slug}`, stakeholderTokenParam);
          setIsStakeholderViewer(true);
        } else {
          const sk = `portal_stakeholder_${slug}`;
          if (sessionStorage.getItem(sk) === 'true') {
            const c = await readStakeholderClaimsFromToken();
            if (c.isStakeholder && c.portalId === accessMeta.portalId) {
              setIsStakeholderViewer(true);
            }
          }
        }

        loadPortalData(docSnap);
      } catch (err) {
        console.error('Error fetching portal:', err);
        setError('Failed to load');
        setLoading(false);
      }
    };

    const loadPortalBySlug = async (realSlug: string) => {
      window.history.replaceState(null, '', `/portal/${realSlug}/questionnaire`);
      const { snap } = await loadPortalSnapBySlug(realSlug);
      if (snap.exists()) {
        loadPortalData(snap);
        return true;
      }
      return false;
    };

    const createAndLoadLead = async () => {
      if (leadCreatedRef.current) return;
      const pending = initialPendingParams.current;

      if (!pending) {
        const p = new URLSearchParams(window.location.search);
        setPendingSignupDefaults({
          firstName: p.get('fn') || undefined,
          lastName: p.get('ln') || undefined,
          email: p.get('email') || undefined,
          location: p.get('loc') || p.get('location') || undefined,
        });
        setShowPendingSignup(true);
        setLoading(false);
        return;
      }

      leadCreatedRef.current = true;

      try {
        const result = await createPublicQuestionnaireLead(pending);
        if (result.ok && (await loadPortalBySlug(result.slug))) {
          return;
        }
        setError('Could not load your questionnaire. Please try again.');
        setLoading(false);
      } catch (err) {
        console.error('Error creating lead:', err);
        setError('Could not load your questionnaire. Please try again.');
        setLoading(false);
      }
    };

    if (slug === 'pending') {
      createAndLoadLead();
    } else {
      fetchBySlug();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, stakeholderTokenParam]);

  const handlePendingSignupSuccess = async ({ slug: realSlug }: { slug: string }) => {
    setShowPendingSignup(false);
    setLoading(true);
    setError(null);
    try {
      await ensureAuth();
      const { snap } = await loadPortalSnapBySlug(realSlug);
      if (snap.exists()) {
        window.history.replaceState(null, '', `/portal/${realSlug}/questionnaire`);
        const data = snap.data();
        setPortalDocId(snap.id);
        setIsPublicLead(true);
        if (data.dealId && !projectId) {
          setResolvedProjectId(data.dealId);
        }
        setPortal({
          id: snap.id,
          slug: data.slug,
          accessPin: data.accessPin,
          clientName: data.clientName,
          clientEmail: data.clientEmail,
          leadId: data.leadId,
          currentStage: data.currentStage || 'design-fee',
          stageHistory: data.stageHistory || [],
          designFeeSkipped: data.designFeeSkipped || false,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
        } as ClientPortal);
        setLoading(false);
        return;
      }
      setError('Could not load your questionnaire. Please try again.');
    } catch (err) {
      console.error('Error loading portal after signup:', err);
      setError('Could not load your questionnaire. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (showPendingSignup) {
    return (
      <QuestionnaireSignupForm
        initialValues={pendingSignupDefaults}
        onSuccess={handlePendingSignupSuccess}
      />
    );
  }

  if (loading) {
    return <LoadingScreen message="Loading..." size="medium" />;
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
        <p>{error}</p>
      </div>
    );
  }

  if (!portal || !portalDocId) return null;

  const handleBack = isPublicLead
    ? () => { window.location.href = 'https://lctimberframes.com'; }
    : () => {
        const p = new URLSearchParams();
        if (effectiveProjectId) p.set('project', effectiveProjectId);
        const tok = stakeholderTokenParam || (slug ? sessionStorage.getItem(`portal_stakeholder_token_${slug}`) : null);
        if (tok) p.set('stakeholderToken', tok);
        const qs = p.toString();
        navigate(`/portal/${slug}${qs ? `?${qs}` : ''}`);
      };

  const effectiveProjectId = projectId || resolvedProjectId || undefined;

  return (
    <DesignQuestionnaire
      portalId={portalDocId}
      leadId={portal.leadId}
      clientName={portal.clientName}
      projectId={effectiveProjectId}
      isPublicLead={isPublicLead}
      onBack={handleBack}
      stakeholderViewOnly={isStakeholderViewer}
    />
  );
};

export default DesignQuestionnairePage;
